import _extends from "@babel/runtime/helpers/extends";
import React, { useState } from "react";
import { Button, Dropdown, Grid, Icon, InputText, Picture, Text } from "~/components-shared";
import ai from "~/assets/i/logos/ai.png";
import styled, { useTheme } from "styled-components";
import { useTranslation } from "react-i18next";
import { AiAssistantRequestsEnum, StatusStateEnum } from "~/types/enums";
import aiContextData from "~/lib/aiContextData";
import AiAssistantContent from "~/components/AiAssistantContent";
import { useDispatch, useSelector } from "react-redux";
import aiQueriesActionCreators from "~/store/aiQueries/actions";
import aiQuerySelectors from "~/store/aiQueries/selectors";
const DropdownList = styled(Dropdown.List)`
  min-height: 80vh;
  max-height: 80vh;
  width: 768px;
  display: flex;
  flex-direction: column;
`;
const StyledButton = styled(Button)`
  transition: transform 200ms ease-in-out, box-shadow 100ms ease-in-out;
  background-color: rgb(40 151 214 / 10%);
  border-radius: 50%;
  box-shadow: ${_ref => {
  let {
    isOpen
  } = _ref;
  return isOpen ? `0px 0px 100vh 100vh rgb(40 151 214 / 50%)` : `0 0 120px 4vh rgb(40 151 214 / 30%)`;
}};
  &:hover {
    transform: scale(1.2);
  }
`;
const StyledPicture = styled(Picture)`
  display: inline-block;
  vertical-align: top;
`;
const TopBar = styled(Grid)`
  position: sticky;
  top: 0;
  background: ${_ref2 => {
  let {
    theme
  } = _ref2;
  return theme.colors.ai.background;
}};
  z-index: 2;
`;
const Content = styled.div`
  flex-grow: 1;
`;
const BottomBar = styled.div`
  position: sticky;
  bottom: 0;
  background: ${_ref3 => {
  let {
    theme
  } = _ref3;
  return theme.colors.background.primary;
}};
  z-index: 2;
`;
const AiAssistantDropdown = _ref4 => {
  let {
    direction = "topLeft",
    ...rest
  } = _ref4;
  const theme = useTheme();
  const dispatch = useDispatch();
  const {
    t
  } = useTranslation();
  const [selectedContext, setSelectedContext] = useState(AiAssistantRequestsEnum.GLOBAL_CONTEXT);
  const [query, setQuery] = useState("");
  const status = useSelector(aiQuerySelectors.getStatusByQuery("+"));
  const [showHistory, setShowHistory] = useState(false);
  const contextData = aiContextData[selectedContext];
  const handleSubmit = () => {
    setShowHistory(true);
    dispatch(aiQueriesActionCreators.createStarted({
      query,
      context: contextData?.context
    }, data => {
      if (data) {
        setQuery("");
      }
    }));
  };
  const handleSuggestionClick = text => {
    setQuery(text);
  };
  const handleContextClick = context => {
    setSelectedContext(context);
  };
  const handleToggleHistory = () => {
    setShowHistory(prev => !prev);
  };
  return /*#__PURE__*/React.createElement(Dropdown, {
    direction: direction,
    customHandler: (toggle, isOpen) => /*#__PURE__*/React.createElement(StyledButton, _extends({
      onClick: e => toggle(e),
      outline: false,
      color: "translucent",
      "data-tooltip-id": "general-tooltip",
      "data-tooltip-content": t("AiAssistantDropdown.yourPersonalAiAssistant"),
      isOpen: isOpen,
      rounded: true,
      s: {
        p: 0
      }
    }, rest), /*#__PURE__*/React.createElement(Picture, {
      src: ai,
      width: "4rem",
      height: "4rem",
      border: "circled"
    })),
    fullWidth: true
  }, _ref5 => {
    let {
      isOpen,
      onItemClick
    } = _ref5;
    return /*#__PURE__*/React.createElement(DropdownList, {
      isOpen: isOpen,
      style: {
        marginBottom: "-50px"
      }
    }, /*#__PURE__*/React.createElement(TopBar, {
      cols: 2,
      template: "1fr auto",
      s: {
        px: 4,
        py: 6
      },
      align: "center"
    }, /*#__PURE__*/React.createElement(Text, {
      element: "span",
      fontSize: 6,
      weight: "semiBold",
      display: "block"
    }, "\uD83D\uDC4B ", t("AiAssistantDropdown.iAmJoyie"), /*#__PURE__*/React.createElement(Text, {
      element: "span",
      color: "textSecondary",
      fontSize: 4,
      s: {
        ml: 2
      }
    }, t("AiAssistantDropdown.yourAiAssistant"))), /*#__PURE__*/React.createElement(Button, {
      "data-tooltip-id": "general-tooltip",
      "data-tooltip-content": t("common.actions.close"),
      onClick: () => onItemClick(),
      color: "transparent",
      rounded: true,
      s: {
        p: 2,
        mx: 0.5
      }
    }, /*#__PURE__*/React.createElement(Icon, {
      scale: 5,
      src: "cross",
      color: theme.colors.text.secondary,
      display: "block"
    }))), /*#__PURE__*/React.createElement(Content, null, /*#__PURE__*/React.createElement(AiAssistantContent, {
      showHistory: showHistory,
      onHideHistory: () => setShowHistory(false),
      contextData: contextData,
      onSuggestionSelect: handleSuggestionClick,
      onContextSelect: handleContextClick
    })), /*#__PURE__*/React.createElement(BottomBar, null, /*#__PURE__*/React.createElement("form", {
      onSubmit: e => {
        e.stopPropagation();
        e.preventDefault();
        handleSubmit();
      }
    }, /*#__PURE__*/React.createElement(Grid, {
      cols: 2,
      template: "1fr auto",
      gap: 3,
      s: {
        px: 6,
        py: 6
      },
      align: "center"
    }, /*#__PURE__*/React.createElement(InputText, {
      value: query,
      onChange: setQuery,
      elemBefore: /*#__PURE__*/React.createElement(StyledPicture, {
        src: ai,
        width: "1.3rem",
        height: "1.3rem",
        s: {
          mr: 2
        }
      }),
      elemAfter: /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(Button, {
        color: showHistory ? "primaryTransparent" : "greyTransparent",
        s: {
          p: 2,
          ml: 1
        },
        outline: false,
        "data-tooltip-id": "general-tooltip",
        "data-tooltip-content": t("AiAssistantDropdown.conversationHistory"),
        onClick: handleToggleHistory,
        rounded: true
      }, /*#__PURE__*/React.createElement(Icon, {
        scale: 5,
        display: "block",
        src: "history"
      })), /*#__PURE__*/React.createElement(Button, {
        color: "transparent",
        s: {
          p: 2,
          mr: -2.5
        },
        loading: status === StatusStateEnum.LOADING,
        disabled: !query,
        onClick: handleSubmit,
        rounded: true
      }, /*#__PURE__*/React.createElement(Icon, {
        scale: 5,
        src: "send",
        color: theme.colors.primary.main,
        display: "block"
      }))),
      placeholder: contextData.placeholder,
      rounded: true,
      clearButton: true,
      autoFocus: true
    })))));
  });
};
export default AiAssistantDropdown;